import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { DateTime } from 'luxon';
import { useFeatureFlag } from 'modules/common/hooks';
import { SignalMetaType } from '../utils/types';
import { notesBackgroundStorage } from '../utils/NotesBackgroundStorage';

type InitSignalContextValuesProps = {
  signalId: string;
  signalMeta: SignalMetaType;
  timerStartTime: DateTime;
};

type NotesBackgroudTimerContextValue = {
  isDialogTimerRunning: boolean;
  running: boolean;
  startTime: DateTime | null;
  signalId: string | null;
  signalMeta: SignalMetaType | null;
  saveStartTime: boolean;
  setDialogTimerRunning: (val: boolean) => void;
  setStartTime: (val: DateTime | null) => void;
  initSignalContextValues: (
    initContextParam: InitSignalContextValuesProps
  ) => void;
  resetSignalContextValues: () => void;
  getCurrentInlineTimerDuration: () => number | null;
  updateBackgroundTimerMeta: (val: string | undefined) => void;
  setSaveStartTime: (val: boolean) => void;
};

const NotesBackgroudTimerContext = React.createContext<NotesBackgroudTimerContextValue | null>(
  null
);

export const useNotesBackgroudTimerContext = (): NotesBackgroudTimerContextValue => {
  const val = useContext(NotesBackgroudTimerContext);
  if (!val) {
    throw new Error(
      'useNotesBackgroudTimerContext must be called within a NotesBackgroudTimerContextProvider'
    );
  }

  return val;
};

type NotesBackgroudTimerContextProviderProps = {
  children: React.ReactNode;
};

export const NotesBackgroudTimerContextProvider: React.FC<NotesBackgroudTimerContextProviderProps> = ({
  children
}) => {
  const [isDialogTimerRunning, setDialogTimerRunning] = useState<boolean>(
    false
  );
  const [saveStartTime, setSaveStartTime] = useState<boolean>(false);
  const [running, setRunning] = useState<boolean>(false);
  const startTimer = useCallback(() => setRunning(true), []);
  const stopTimer = useCallback(() => setRunning(false), []);
  const [startTime, setStartTime] = useState<DateTime | null>(null);
  const [signalId, setSignalId] = useState<string | null>(null);
  const [signalMeta, setSignalMeta] = useState<SignalMetaType | null>(null);
  const isTiwInlineTimerEnabled = useFeatureFlag('isTiwInlineTimerEnabled');

  const initSignalContextValues = useCallback(
    ({
      signalId,
      signalMeta,
      timerStartTime
    }: InitSignalContextValuesProps) => {
      setSignalId(signalId);
      setSignalMeta(signalMeta);
      if (!startTime) setStartTime(timerStartTime);
      startTimer();
    },
    [startTime, startTimer]
  );

  const resetSignalContextValues = useCallback(() => {
    stopTimer();
    setSignalId(null);
    setSignalMeta(null);
    setStartTime(null);
  }, [stopTimer]);

  const updateBackgroundTimerMeta = useCallback((description?: string) => {
    const { get, set } = notesBackgroundStorage;
    const localstoreNoteData = get();
    if (!localstoreNoteData) return;
    const { id, meta, timerStartTime } = localstoreNoteData;
    const updatedMeta = { ...meta, description };
    setSignalMeta(updatedMeta);
    set({
      id: id,
      meta: updatedMeta,
      timerStartTime: timerStartTime
    });
  }, []);

  const getCurrentInlineTimerDuration = useCallback(() => {
    if (!signalMeta || !startTime) return null;
    const durationFromCurrentTime = DateTime.local().diff(startTime, 'seconds');
    const durationInSeconds = Number(
      durationFromCurrentTime.seconds.toFixed(0)
    );
    return signalMeta?.duration
      ? durationInSeconds + Number(signalMeta.duration)
      : durationInSeconds;
  }, [signalMeta, startTime]);

  useEffect(() => {
    if (!isTiwInlineTimerEnabled) return;
    const { get } = notesBackgroundStorage;
    const localstoreNoteData = get();
    if (!localstoreNoteData) return;
    const { id, meta, timerStartTime } = localstoreNoteData;
    initSignalContextValues({
      signalId: id,
      signalMeta: meta,
      timerStartTime: DateTime.fromSeconds(timerStartTime)
    });
  }, [initSignalContextValues, isTiwInlineTimerEnabled]);

  return (
    <NotesBackgroudTimerContext.Provider
      value={useMemo(
        () => ({
          isDialogTimerRunning,
          running,
          startTime,
          signalId,
          signalMeta,
          saveStartTime,
          initSignalContextValues,
          resetSignalContextValues,
          getCurrentInlineTimerDuration,
          setDialogTimerRunning,
          updateBackgroundTimerMeta,
          setStartTime,
          setSaveStartTime
        }),
        [
          isDialogTimerRunning,
          running,
          startTime,
          signalMeta,
          signalId,
          saveStartTime,
          initSignalContextValues,
          resetSignalContextValues,
          getCurrentInlineTimerDuration,
          setDialogTimerRunning,
          updateBackgroundTimerMeta,
          setStartTime,
          setSaveStartTime
        ]
      )}
    >
      {children}
    </NotesBackgroudTimerContext.Provider>
  );
};
